import React, { useEffect, useRef, useState } from 'react'

import DevLog from '../../modules/devlog/Module'
import LocaleModule from '../../modules/locale/Module'

import { IAction } from '../../actions/types'
import { ILocaleContext } from '../../modules/locale/types'
import { ISessionUseCurrentLocalesState } from '../types'

import { DEFAULT_LOCALE_CONTEXT } from '../../modules/locale/Locale.Context'

const DEBUG_LOCALE_STATE = 'development' === process.env.NODE_ENV

const useCurrentLocalesState: ISessionUseCurrentLocalesState = (currentActionState, mountedRef, modulesLoaded) => {
  const [currentLocales, setCurrentLocales] = useState<ILocaleContext>(DEFAULT_LOCALE_CONTEXT)
  const currentLocalesRef = useRef<ILocaleContext>(currentLocales)
  currentLocalesRef.current = currentLocales

  const lastActionRef = useRef<IAction>(null)
  if (modulesLoaded && lastActionRef.current !== currentActionState.currentActionRef.current) {
    updateLocalesFromServer(lastActionRef, currentActionState.currentActionRef, currentLocalesRef)
  }

  useEffect(() => {
    // sync currentLocalesRef with currentLocales
    if (
      currentLocales.interfaceLocaleID !== currentLocalesRef.current.interfaceLocaleID ||
      currentLocales.surveyLocaleID !== currentLocalesRef.current.surveyLocaleID
    ) {
      setCurrentLocales(currentLocalesRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocales, currentLocalesRef.current])

  useEffect(() => {
    if (modulesLoaded) {
      loadLocaleDicts(currentLocales, setCurrentLocales, mountedRef)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocales, modulesLoaded])

  return {
    currentLocalesRef,
    setCurrentLocales,
    localesLoaded:
      LocaleModule.hasLoadedLocale(currentLocalesRef.current.surveyLocaleID) &&
      LocaleModule.hasLoadedLocale(currentLocalesRef.current.interfaceLocaleID)
  } as const
}

export default useCurrentLocalesState

function updateLocalesFromServer(
  lastActionRef: React.MutableRefObject<IAction>,
  currentActionRef: React.MutableRefObject<IAction>,
  currentLocalesRef: React.MutableRefObject<ILocaleContext>
) {
  lastActionRef.current = currentActionRef.current
  const locale = currentActionRef.current?.data?.locale
  if (locale) {
    const { survey_locale: surveyLocaleID, aytm_locale: interfaceLocaleID, dict } = locale

    //TODO: refactor action locales, current logic - apply first locale as interfaceLocale and surveyLocale, then update surveyLocale if interfaceLocale is already present
    if (dict) {
      if (surveyLocaleID && surveyLocaleID === interfaceLocaleID) {
        LocaleModule.setLocale(surveyLocaleID, { dict, setUpdatedFromServer: true })
      } else if (interfaceLocaleID && !LocaleModule.hasUpdatedFromServerLocale(interfaceLocaleID)) {
        LocaleModule.setLocale(interfaceLocaleID, { dict, setUpdatedFromServer: true })
        if (surveyLocaleID) {
          LocaleModule.setLocale(surveyLocaleID, { dict, setUpdatedFromServer: true })
        }
      } else if (surveyLocaleID) {
        LocaleModule.setLocale(surveyLocaleID, { dict, setUpdatedFromServer: true })
      }
    }
    // update context
    if (
      (interfaceLocaleID || surveyLocaleID) &&
      (currentLocalesRef.current.interfaceLocaleID !== interfaceLocaleID ||
        currentLocalesRef.current.surveyLocaleID !== surveyLocaleID)
    ) {
      if (DEBUG_LOCALE_STATE) {
        DevLog._log('SW(locale):', 'change', { interfaceLocaleID, surveyLocaleID })
      }
      currentLocalesRef.current = {
        interfaceLocaleID: interfaceLocaleID ? interfaceLocaleID : currentLocalesRef.current.interfaceLocaleID,
        surveyLocaleID: surveyLocaleID ? surveyLocaleID : currentLocalesRef.current.surveyLocaleID
      }
    }
  }
}

function loadLocaleDicts(
  currentLocales: ILocaleContext,
  setCurrentLocales: ReturnType<typeof React.useState<ILocaleContext>>[1],
  mountedRef: React.MutableRefObject<boolean>
) {
  if (
    !LocaleModule.hasLoadedLocale(currentLocales.interfaceLocaleID, true) ||
    !LocaleModule.hasLoadedLocale(currentLocales.surveyLocaleID, true)
  ) {
    Promise.all([
      !LocaleModule.hasLoadedLocale(currentLocales.interfaceLocaleID, true)
        ? LocaleModule.loadLocaleDict(currentLocales.interfaceLocaleID)
        : null,
      currentLocales.interfaceLocaleID !== currentLocales.surveyLocaleID &&
      !LocaleModule.hasLoadedLocale(currentLocales.surveyLocaleID, true)
        ? LocaleModule.loadLocaleDict(currentLocales.surveyLocaleID)
        : null
    ])
      .then(() => {
        if (mountedRef.current) {
          setCurrentLocales(currentLocales => {
            return { ...currentLocales }
          })
        }
      })
      .catch(error => {
        DevLog.error(error, 'SW[loadLocaleDicts]', 'Session State: failed to load locale dicts')
      })
  }
}
